import { forwardRef, useId } from "react";
import { cn } from "@/lib/cn";

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  isPrimary?: boolean;
  onPrimaryChange?: (isPrimary: boolean) => void;
  isSafe?: boolean;
  onSafeChange?: (isSafe: boolean) => void;
  showPrimary?: boolean;
  showSafe?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function unformatPhone(formatted: string): string {
  return formatted.replace(/\D/g, "");
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = "",
      onChange,
      onBlur,
      label,
      error,
      required,
      disabled,
      isPrimary,
      onPrimaryChange,
      isSafe,
      onSafeChange,
      showPrimary = false,
      showSafe = false,
      id: idProp,
      name,
      className,
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const raw = unformatPhone(e.target.value);
      onChange?.(raw);
    }

    const displayValue = value ? formatPhone(value) : "";

    return (
      <div className={cn("space-y-1.5", className)}>
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium leading-none text-foreground"
          >
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </label>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={ref}
            type="tel"
            id={id}
            name={name}
            value={displayValue}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            required={required}
            placeholder="(555) 123-4567"
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            className={cn(
              "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              error && "border-destructive focus-visible:ring-destructive",
            )}
          />
          {showPrimary && (
            <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="radio"
                checked={isPrimary}
                onChange={(e) => onPrimaryChange?.(e.target.checked)}
                disabled={disabled}
                className="h-3.5 w-3.5 accent-primary"
              />
              Primary
            </label>
          )}
          {showSafe && (
            <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={isSafe}
                onChange={(e) => onSafeChange?.(e.target.checked)}
                disabled={disabled}
                className="h-3.5 w-3.5 rounded accent-primary"
              />
              Safe
            </label>
          )}
        </div>
        {error && (
          <p id={`${id}-error`} className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  },
);
PhoneInput.displayName = "PhoneInput";

export { PhoneInput, formatPhone, unformatPhone };
